import type {
  CustomerProfileCandidateDto,
  CustomerProfileResearchHints,
} from './api/domains/customerRequests';

/**
 * Formularstand von „Neuen Kunden vorschlagen" samt der Felder, die KIAN ausgefüllt hat.
 *
 * Regeln fürs Übernehmen eines Rechercheergebnisses:
 * - Was der Techniker getippt hat, bleibt stehen. KIAN füllt nur leere Felder — und die,
 *   die es selbst gefüllt hat und die seither niemand angefasst hat. So überschreibt die
 *   Wahl eines anderen Treffers den vorigen, nie eine eigene Eingabe.
 * - Ausnahme Firmenname: Der getippte Name war der Suchbegriff („Boxermotor"), der
 *   gefundene ist der offizielle mit Rechtsform — der gehört in den Vorschlag.
 * - Hatte der vorige Treffer ein Feld, der neue nicht, wird es wieder leer: sonst stünde
 *   die Telefonnummer von Firma A bei Firma B.
 */
export const DRAFT_FIELDS = [
  'companyName',
  'contactName',
  'email',
  'phone',
  'website',
  'street',
  'zipCode',
  'city',
  'notes',
] as const;
export type DraftField = (typeof DRAFT_FIELDS)[number];

export interface ProposeCustomerDraft {
  values: Record<DraftField, string>;
  /** Von KIAN gefüllt, seither unverändert → Hinweistext unter dem Feld. */
  kian: Partial<Record<DraftField, string>>;
}

export const EMPTY_DRAFT: ProposeCustomerDraft = {
  values: {
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    street: '',
    zipCode: '',
    city: '',
    notes: '',
  },
  kian: {},
};

const FILLABLE = ['companyName', 'contactName', 'email', 'phone', 'website', 'street', 'zipCode', 'city'] as const;

export function editDraft(draft: ProposeCustomerDraft, field: DraftField, value: string): ProposeCustomerDraft {
  const kian = { ...draft.kian };
  delete kian[field];
  return { values: { ...draft.values, [field]: value }, kian };
}

export function applyCandidate(
  draft: ProposeCustomerDraft,
  candidate: CustomerProfileCandidateDto
): ProposeCustomerDraft {
  const values = { ...draft.values };
  const kian = { ...draft.kian };
  for (const field of FILLABLE) {
    const found = candidate[field]?.trim() ?? '';
    const ownedByKian = kian[field] !== undefined;
    if (!found) {
      if (ownedByKian) {
        values[field] = '';
        delete kian[field];
      }
      continue;
    }
    const current = values[field].trim();
    if (current === found) continue;
    if (field === 'companyName') {
      // Beim Wechsel des Treffers bleibt der Hinweis auf die ursprüngliche Eingabe stehen.
      if (!ownedByKian) kian[field] = current ? `von KIAN · eingegeben war „${current}"` : 'von KIAN';
    } else {
      if (current && !ownedByKian) continue;
      kian[field] =
        field === 'contactName' && candidate.contactRole
          ? `von KIAN · ${candidate.contactRole}`
          : 'von KIAN';
    }
    values[field] = found;
  }
  return { values, kian };
}

/**
 * Suchhinweise: nur, was der Techniker selbst eingetragen hat. KIANs eigene Werte
 * zurückzugeben, zöge eine zweite Suche zum ersten Treffer zurück — gerade dann, wenn
 * jemand „andere Firma" sucht. Der Firmenname geht immer mit.
 */
export function researchHints(draft: ProposeCustomerDraft): CustomerProfileResearchHints {
  const own = (field: DraftField) =>
    draft.kian[field] === undefined && draft.values[field].trim() ? draft.values[field].trim() : undefined;
  return {
    companyName: draft.values.companyName.trim(),
    city: own('city'),
    email: own('email'),
    website: own('website'),
    phone: own('phone'),
    contactName: own('contactName'),
  };
}

const NOTES_MAX = 2000;

/** Die Quellen gehen an die Admins mit — daran prüfen sie den Vorschlag vor der Freigabe. */
export function notesWithSources(notes: string, applied: CustomerProfileCandidateDto | null): string {
  const own = notes.trim();
  if (!applied || applied.sources.length === 0) return own;
  const line = `Von KIAN recherchiert, Quellen: ${applied.sources.join(', ')}`;
  const joined = own ? `${own}\n\n${line}` : line;
  return joined.slice(0, NOTES_MAX);
}
